import api from "./client";

/** What a label backfill from a date would move (storage-maintenance.service.ts). */
export type LabelBackfillPreview = {
  from: string | null;
  cutover: string;
  /** Why the date cannot be used; nothing is counted when set. */
  problem: string | null;
  labels: number;
  companies: number;
  nights: number;
};

export const admin = {
  getUsers: (page: number = 1, limit: number = 20, search?: string, role?: string, status?: string) =>
    api
      .get(
        `/users?page=${page}&limit=${limit}${search ? `&search=${search}` : ""}${role ? `&role=${role}` : ""}${status ? `&status=${status}` : ""}`,
      )
      .then((res) => res.data),
  getCompanies: (page: number = 1, limit: number = 20, search?: string, status?: string) =>
    api
      .get(
        `/companies?page=${page}&limit=${limit}${search ? `&search=${search}` : ""}${status ? `&status=${status}` : ""}`,
      )
      .then((res) => res.data),
  updateCompanyStatus: (companyId: string, isActive: boolean) =>
    api.patch(`/companies/${companyId}/status`, { isActive }).then((res) => res.data),
  updateCompany: (companyId: string, data: any) =>
    api.patch(`/companies/${companyId}`, data).then((res) => res.data),
  getCompanyProfile: (companyId: string) =>
    api.get(`/companies/${companyId}/profile`).then((res) => res.data),
  getCompanyCarriers: (companyId?: string) =>
    api
      .get(
        `/company-carriers${companyId ? `?companyId=${companyId}` : ""}`,
      )
      .then((res) => res.data),
  getCredentials: (companyId: string, carrierId: string) =>
    api
      .get(`/credentials/${companyId}/${carrierId}`)
      .then((res) => res.data),
  getLogs: (params: any) =>
    api.get("/http-logs", { params }).then((res) => res.data),
  getLog: (id: string) => api.get(`/http-logs/${id}`).then((res) => res.data),
  getLogStats: () => api.get("/http-logs/stats").then((res) => res.data),
  getLogStructure: () => api.get("/system-logs/structure").then((res) => res.data),
  getLogFiles: (company?: string, date?: string) => {
    let url = "/system-logs/files";
    const params: string[] = [];
    if (company) params.push(`company=${encodeURIComponent(company)}`);
    if (date) params.push(`date=${encodeURIComponent(date)}`);
    if (params.length > 0) url += `?${params.join("&")}`;
    return api.get(url).then((res) => res.data);
  },
  getSystemLogs: (filename: string, page: number = 1, limit: number = 50, filters: any = {}, company?: string, date?: string) => {
    let url = `/system-logs?filename=${filename}&page=${page}&limit=${limit}`;
    if (company) url += `&company=${encodeURIComponent(company)}`;
    if (date) url += `&date=${encodeURIComponent(date)}`;
    if (filters.method) url += `&method=${filters.method}`;
    if (filters.status) url += `&status=${filters.status}`;
    if (filters.carrier) url += `&carrier=${filters.carrier}`;
    if (filters.endpoint) url += `&endpoint=${encodeURIComponent(filters.endpoint)}`;
    if (filters.ip) url += `&ip=${encodeURIComponent(filters.ip)}`;
    if (filters.requestSearch) url += `&requestSearch=${encodeURIComponent(filters.requestSearch)}`;
    if (filters.responseSearch) url += `&responseSearch=${encodeURIComponent(filters.responseSearch)}`;
    if (filters.headerSearch) url += `&headerSearch=${encodeURIComponent(filters.headerSearch)}`;
    return api.get(url).then((res) => res.data);
  },
  getSystemLogEntry: (filename: string, line: number, company?: string, date?: string) => {
    let url = `/system-logs/entry?filename=${filename}&line=${line}`;
    if (company) url += `&company=${encodeURIComponent(company)}`;
    if (date) url += `&date=${encodeURIComponent(date)}`;
    return api.get(url).then((res) => res.data);
  },
  clearSystemLogs: (filename: string, company?: string, date?: string) => {
    let url = `/system-logs?filename=${filename}`;
    if (company) url += `&company=${encodeURIComponent(company)}`;
    if (date) url += `&date=${encodeURIComponent(date)}`;
    return api.delete(url).then((res) => res.data);
  },
  deleteUser: (userId: string) =>
    api.delete(`/users/${userId}`).then((res) => res.data),
  updateUserStatus: (userId: string, status: string) =>
    api.patch(`/users/${userId}`, { status }).then((res) => res.data),
  updateUser: (userId: string, data: { status?: string; email?: string; name?: string }) =>
    api.patch(`/users/${userId}`, data).then((res) => res.data),
  getAllCarriers: () => api.get("/carriers").then((res) => res.data),
  createCarrier: (data: any) =>
    api.post("/carriers", data).then((res) => res.data),
  toggleCarrier: (carrierId: string, isActive: boolean) =>
    api
      .patch(`/carriers/${carrierId}`, { isActive })
      .then((res) => res.data),
  updateCarrier: (carrierId: string, data: any) =>
    api
      .patch(`/carriers/${carrierId}/edit`, data)
      .then((res) => res.data),
  deleteCarrier: (carrierId: string) =>
    api.delete(`/carriers/${carrierId}`).then((res) => res.data),
  // Seeds one global carrier row per carrier on the ShipStation account.
  // Master rows only — each company supplies its own API key afterwards under
  // Carrier Integrations.
  syncShipStationCarriers: (
    apiKey: string,
    apiSecret?: string,
    apiVersion?: string,
  ) =>
    api
      .post("/carriers/shipstation/sync", { apiKey, apiSecret, apiVersion })
      .then((res) => res.data),
  getCoreSettings: () =>
    api.get("/core-settings").then((res) => res.data),
  updateCoreSettings: (data: any) =>
    api.patch("/core-settings", data).then((res) => res.data),
  /** What a batch-file retention of `days` would delete at the next nightly cleanup. */
  previewStorageRetention: (days: number) =>
    api
      .get("/core-settings/storage/preview", { params: { days } })
      .then((res) => res.data as { days: number; batches: number; files: number }),
  previewLabelBackfill: (from: string) =>
    api
      .get("/core-settings/storage/label-backfill/preview", { params: { from } })
      .then((res) => res.data as LabelBackfillPreview),
  /** Moves the label cut-over back to `from`; the nightly job does the moving. */
  startLabelBackfill: (from: string) =>
    api
      .post("/core-settings/storage/label-backfill", { from })
      .then((res) => res.data as { labelArchiveFrom: string }),
  /** Status only — the server never returns the master password or its hash. */
  getMasterAccess: () =>
    api.get("/master-access").then((res) => res.data),
  updateMasterAccess: (data: { enabled?: boolean; newPassword?: string }) =>
    api.patch("/master-access", data).then((res) => res.data),
  testEmail: (settings: any, recipientEmail: string) =>
    api
      .post("/test-email", { settings, recipientEmail })
      .then((res) => res.data),
  /** Sends with the credentials passed in, not the stored ones — usable before saving. */
  testSms: (settings: any, recipientNumber: string) =>
    api
      .post("/test-sms", { settings, recipientNumber })
      .then((res) => res.data),
  clearCache: () =>
    api.post("/clear-cache").then((res) => res.data),
  getPublicSettings: () =>
    api.get("/public-settings").then((res) => res.data),
  uploadSystemLogo: (formData: FormData) =>
    api
      .post("/core-settings/logo", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => res.data),
  uploadWebsiteLogo: (formData: FormData) =>
    api
      .post("/core-settings/website-logo", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => res.data),
  getSmtpLogs: (page: number = 1, limit: number = 50, search?: string, status?: string, companyId?: string) => {
    let url = `/smtp-logs?page=${page}&limit=${limit}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (status) url += `&status=${status}`;
    if (companyId) url += `&companyId=${companyId}`;
    return api.get(url).then((res) => res.data);
  },
  deleteAllSmtpLogs: () => api.delete('/smtp-logs').then((res) => res.data),
  getSmsLogs: (page: number = 1, limit: number = 50, search?: string, status?: string, companyId?: string) => {
    let url = `/sms-logs?page=${page}&limit=${limit}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (status) url += `&status=${status}`;
    if (companyId) url += `&companyId=${companyId}`;
    return api.get(url).then((res) => res.data);
  },
  deleteAllSmsLogs: () => api.delete('/sms-logs').then((res) => res.data),
  getCronSchedules: (page: number = 1, limit: number = 50, search?: string, status?: string, companyId?: string) => {
    let url = `/cron-schedules?page=${page}&limit=${limit}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (status) url += `&status=${status}`;
    if (companyId) url += `&companyId=${companyId}`;
    return api.get(url).then((res) => res.data);
  },
  deleteAllCronSchedules: () => api.delete('/cron-schedules').then((res) => res.data),
  regenerateCronSchedules: () => api.post('/cron-schedules/regenerate').then((res) => res.data),
  getNotificationRules: () => api.get("/notification-rules").then((res) => res.data),
  createNotificationRule: (data: any) => api.post("/notification-rules", data).then((res) => res.data),
  updateNotificationRule: (id: string, data: any) => api.put(`/notification-rules/${id}`, data).then((res) => res.data),
  deleteNotificationRule: (id: string) => api.delete(`/notification-rules/${id}`).then((res) => res.data),
  getStripeSettings: () => api.get('/settings/payment/stripe').then((res) => res.data),
  updateStripeSettings: (data: any) => api.put('/settings/payment/stripe', data).then((res) => res.data),
  testStripeConnection: (data: any) => api.post('/settings/payment/stripe/test', data).then((res) => res.data),
};
